import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthState, OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { useNavigate } from "react-router";
import { t } from "ttag";

import { AlertStatus } from "merchant-portal-common/Alert";
import { tracking } from "merchant-portal-frontend/src/analytics";
import { getConfig } from "merchant-portal-frontend/src/config";
import { GrantScope, ScopePermissions } from "merchant-portal-frontend/src/global.constants";
import { browserLogs } from "merchant-portal-frontend/src/logger";
import {
	LoginOptions,
	shouldUseFakeAuth,
	useFakeAuth,
	useOkta,
} from "merchant-portal-frontend/src/model/auth";
import { BreadToken } from "merchant-portal-frontend/src/model/common/auth";
import { routes } from "merchant-portal-pages/routes";
import { useTheme } from "merchant-portal-theme/useTheme";

import { useAlert, useAlertContext } from "./AlertProvider";

export const determineScopePermissions = (scopes: string[]) => {
	const containsReadWriteScope = scopes.some((scope) => scope && scope.substr(0, 4) === "c:tx");
	return containsReadWriteScope ? ScopePermissions.ReadWrite : ScopePermissions.Read;
};

export interface AuthContextProps {
	isAuthenticated: boolean;
	login: (opts?: LoginOptions) => Promise<void>;
	logout: () => Promise<void>;
	refreshBreadToken: () => Promise<string>;
	breadTokenExchange: (selectedMerchantDetailsId: string) => Promise<string>;
	currentScopePermissions: ScopePermissions;
	hasGrantAccess: (scope: GrantScope) => boolean;
	breadToken: BreadToken | null;
	authState: AuthState | null;
	oktaAuth: OktaAuth | null;
	justLoggedIn: boolean;
	setJustLoggedIn: (value: boolean) => void;
}

export const AuthContext = React.createContext<AuthContextProps>({
	authState: null,
	isAuthenticated: false,
	login: async () => {
		/* do nothing. */
	},
	logout: async () => {
		/* do nothing. */
	},
	refreshBreadToken: async () => "",
	breadTokenExchange: async () => "",
	breadToken: null,
	oktaAuth: null,
	currentScopePermissions: ScopePermissions.Read,
	hasGrantAccess: () => false,
	justLoggedIn: false,
	setJustLoggedIn: () => {},
});

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const navigate = useNavigate();
	const alert = useAlert();
	const { defaultErrorMessage } = useTheme();
	const { dismissAlert } = useAlertContext();

	const oktaAuth = useRef<OktaAuth | null>(
		shouldUseFakeAuth()
			? null
			: new OktaAuth({
					tokenManager: {
						autoRenew: false,
						autoRemove: false,
						syncStorage: true,
					},
					issuer: getConfig("oktaissuer"),
					clientId: getConfig("oktaclientid"),
					redirectUri: `${window.location.origin}${routes.loginCallback}`,
					postLogoutRedirectUri: `${window.location.origin}${routes.logoutCallback}`,
					restoreOriginalUri: async (_oktaAuth, originalUri) => {
						navigate(toRelativeUrl(originalUri, window.location.origin));
					},
				}),
	);

	const [authState, setAuthState] = useState<AuthState | null>(null);

	const authHook = shouldUseFakeAuth() ? useFakeAuth : useOkta;

	const { breadToken, isAuthenticated, login, logout, refreshBreadToken, breadTokenExchange } =
		authHook({
			oktaAuth: oktaAuth.current,
			authState,
		});

	const [justLoggedIn, setJustLoggedIn] = useState(false);
	const isFirstRender = useRef(true);
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
		} else if (isAuthenticated) {
			setJustLoggedIn(true);
		}
	}, [isAuthenticated]);

	const currentScopePermissions = useMemo(
		() => determineScopePermissions(breadToken?.scopes || []),
		[breadToken],
	);

	const hasGrantAccess = useCallback(
		(scope: GrantScope) => {
			const scopes = breadToken?.scopes || [];

			return scopes.includes(scope);
		},
		[breadToken],
	);

	const showAuthError = useCallback(
		(error: Error, logMessage: string) => {
			browserLogs.logger.error({
				message: `${logMessage}: ${error.message}`,
				context: error,
			});

			alert({
				message: [defaultErrorMessage, t`Error was: ${error.message}`],
				status: AlertStatus.Error,
				duration: 20000,
			});
		},
		[defaultErrorMessage, alert],
	);

	useEffect(() => {
		const currentOktaAuth = oktaAuth.current;
		if (!shouldUseFakeAuth() && currentOktaAuth) {
			currentOktaAuth.authStateManager.subscribe((state: AuthState) => {
				setAuthState(state);
				if (state?.error) {
					showAuthError(state.error, "authState error");
				}
			});
			currentOktaAuth.start();
			if (currentOktaAuth.token?.isLoginRedirect()) {
				currentOktaAuth.token
					.parseFromUrl()
					.then(({ tokens }) => {
						currentOktaAuth.tokenManager.setTokens(tokens);
						refreshBreadToken({ oktaToken: tokens?.accessToken?.accessToken })
							.then(() => {
								navigate(currentOktaAuth.getOriginalUri() || "/");
							})
							.catch((e) => {
								navigate(routes.login);
								showAuthError(e, t`Failed to refresh okta token for bread token`);
							});
						tracking.trackLoginSuccess();
					})
					.catch((e) => {
						navigate(routes.login);
						showAuthError(e, t`Failed to parse token from url`);
					});
			}
		}

		return () => {
			if (!shouldUseFakeAuth() && currentOktaAuth) {
				currentOktaAuth.authStateManager.unsubscribe();
				currentOktaAuth.stop();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // this is so we're not unsubscribing / resubscribing which can miss updates

	const trackingRef = useRef(false);

	useEffect(() => {
		if (breadToken && !trackingRef.current) {
			const { cid, tenantID, sub, uid } = breadToken.claims;
			tracking.registerCurrentMerchantAndTenant(cid, tenantID, sub, uid);
			trackingRef.current = true;
		}
	}, [breadToken]);

	return (
		<AuthContext.Provider
			value={{
				breadToken,
				isAuthenticated,
				login: async (opts: LoginOptions) => {
					dismissAlert();
					await login(opts);
				},
				logout: async () => {
					setAuthState(null);
					await logout();
				},
				authState,
				refreshBreadToken,
				breadTokenExchange,
				oktaAuth: oktaAuth.current,
				currentScopePermissions,
				hasGrantAccess,
				justLoggedIn,
				setJustLoggedIn,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
